import { Boxes, ArrowRight } from "lucide-react";
import { theme, emptyFrame } from "../theme";
import { PortalLink } from "./PortalLink";
import { ASSET_KINDS, labelOf, SeverityPill, StatusPill } from "../pages/resilience/shared";

// The supplier side of Asset.supplierId (docs/OPERATIONAL-RESILIENCE.md, "Supplier
// lifecycle and the asset register"): what a supplier delivers, as GET /tprm/suppliers
// and GET /tprm/suppliers/:id report it, and what an ended relationship leaves behind.
// Discovery's own supplier_service row is retired when the supplier goes; a member-linked
// asset stays and the Resilience attention list raises it.

// ── Types (mirror backend/src/lib/supplierAssets.ts) ──────────────────────────

export interface SupplierAsset {
  id:                  string;
  name:                string;
  kind:                string;
  criticality:         string;
  isEssentialFunction: boolean;
  status:              string;     // active | retired
}

/** What PUT /tprm/suppliers/:id (status ended) and POST .../reject report back. */
export interface DependentAssets {
  active:    number;
  important: number;               // critical / high / essential-function
  names:     string[];
}

const ENDED = new Set(["inactive", "offboarded", "rejected"]);
const CRITICALITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

// ── Helpers ───────────────────────────────────────────────────────────────────

const isCritical = (a: SupplierAsset) => a.criticality === "critical" || a.isEssentialFunction;

/** "delivers 3 assets (1 critical)" for the supplier list, or null when nothing active is linked. */
export function deliversLabel(assets: SupplierAsset[] | undefined): string | null {
  const active = (assets ?? []).filter((a) => a.status === "active");
  if (active.length === 0) return null;
  const critical = active.filter(isCritical).length;
  return `delivers ${active.length} asset${active.length === 1 ? "" : "s"}${critical ? ` (${critical} critical)` : ""}`;
}

/** The notice shown after a status change ends the relationship. */
export function dependentAssetsNotice(supplierName: string, status: string, d: DependentAssets): string | null {
  if (d.active === 0) return null;
  const names = d.names.join(", ") + (d.active > d.names.length ? ", ..." : "");
  return `${supplierName} is now ${status.replace(/_/g, " ")} but ${d.active} active asset${d.active === 1 ? "" : "s"} still depend${d.active === 1 ? "s" : ""} on it${d.important ? ` (${d.important} critical, high or essential)` : ""}: ${names}. Replace the supplier on each asset or retire it in the asset register.`;
}

// ── Panel ─────────────────────────────────────────────────────────────────────

interface Props {
  supplier: { id: string; name: string; status: string; riskTier: string; assets?: SupplierAsset[] };
}

export function SupplierAssetsPanel({ supplier }: Props) {
  const assets = [...(supplier.assets ?? [])].sort((a, b) => (a.status === b.status ? 0 : a.status === "active" ? -1 : 1) || (CRITICALITY_RANK[a.criticality] ?? 9) - (CRITICALITY_RANK[b.criticality] ?? 9) || a.name.localeCompare(b.name));
  const active = assets.filter((a) => a.status === "active");
  const ended = ENDED.has(supplier.status);
  const tierRank = CRITICALITY_RANK[supplier.riskTier] ?? 2;

  return (
    <>
      <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 3, background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
        The assets this supplier delivers or hosts, from the operational resilience asset register: the supplier-delivered service the register discovers for every active supplier, plus any asset a member marked as delivered by it. Offboarding the supplier retires only the discovered service; everything else stays and is raised on the Resilience attention list until it is moved to another supplier or retired.
      </div>

      {ended && active.length > 0 && (
        <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 3, background: "var(--c-warningBg)", color: "var(--c-warningText)", fontSize: 12, lineHeight: 1.5 }}>
          This supplier is {supplier.status.replace(/_/g, " ")} but {active.length} active asset{active.length === 1 ? "" : "s"} still depend{active.length === 1 ? "s" : ""} on it. Replace the supplier on each asset or retire it.
        </div>
      )}

      {assets.length === 0 ? (
        <div style={{ ...emptyFrame, fontSize: 13, color: theme.color.muted, marginBottom: 14 }}>
          No assets are linked to this supplier yet. Assets appear here when the register discovers the supplier's service or a member picks this supplier under "Delivered by supplier" on an asset.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {assets.map((a) => {
            const belowTier = a.status === "active" && !ended && tierRank < (CRITICALITY_RANK[a.criticality] ?? 9);
            return (
              <div key={a.id} style={{ padding: "10px 12px", border: "1px solid var(--c-border)", borderRadius: 3, opacity: a.status === "retired" ? 0.7 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.text, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <Boxes size={14} style={{ color: theme.color.muted, flexShrink: 0 }} />
                    {a.name}
                    {a.isEssentialFunction && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--c-accentText)" }}>ESSENTIAL</span>}
                    {a.status === "retired" && <StatusPill value="retired" />}
                  </div>
                  <SeverityPill value={a.criticality} />
                </div>
                <div style={{ fontSize: 12, color: theme.color.muted, marginTop: 4 }}>
                  {labelOf(ASSET_KINDS, a.kind)}
                  {belowTier && <span style={{ color: "var(--c-warningText)" }}> · rated {a.criticality} while the supplier is {supplier.riskTier} — review its criticality</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PortalLink to="/resilience/assets" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--c-accentText)", textDecoration: "none", fontWeight: 600 }}>
        Open the asset register <ArrowRight size={14} />
      </PortalLink>
    </>
  );
}
